import React, { useRef, useState, useEffect } from 'react'
import styled from 'styled-components'
import InfiniteScroll from 'react-infinite-scroller'
import RecipeCard from './RecipeCard'
import { EventTrackingConsumer } from '../EventTrackingProvider/EventTrackingProvider'
import { trackPromoImpressions } from '../../lib/trackClicksAndImpressions'
import { CheckoutAddressConsumer } from '../CheckoutAddressProvider/CheckoutAddressProvider'
import {
  createNewObserver,
  impressionTypes,
  trackingTypes,
  triggerImpressionsTracking,
} from '../../lib/impressionsTracker'
import {
  GTM_ENV_VAR as GEV,
  getNCheckFn as getNCheck,
  SPLIT_EVENT,
} from '../../lib/eventTrackerFunction'
import Loader from '../Loader/Loader'

const List = styled.ul`
  display: flex;
  flex-wrap: wrap;
`

const Header = styled.div``

const HeaderTitle = styled.div`
  font-size: 1.125rem;
  line-height: 1.75rem;
  font-weight: bold;
`

const ResultDescription = styled.div`
  font-size: 0.75rem;
  color: #696969;
  font-weight: bold;
  line-height: 1rem;
  margin-bottom: 1rem;
`

const LoaderContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 1.5rem 0;
`

const RecipeList = ({
  items,
  headerTitle,
  headerDescription,
  loadMore,
  hasMore,
  track,
  label,
  pageType,
  checkoutAddress,
  trackSplit,
  ctaLocationMeta,
}) => {
  const [trackedBanners, setTrackedBanners] = useState(false)
  const mounted = useRef()
  const trackBannerImpressions = (
    params = {
      action: '',
      prevStoreId: '',
      prevDBPStoreId: '',
      asset_type: 'Lifestyle',
      page_type: 'Recipe Listing',
    }
  ) => {
    const storeIdToTrack =
      params.prevStoreId ||
      getNCheck(checkoutAddress, 'clientId', '').toString()
    const dbpStoreIdToTrack =
      params.prevDBPStoreId ||
      getNCheck(checkoutAddress, 'storeId', '').toString()
    trackPromoImpressions({
      track,
      trackSplit,
      storeIdToTrack,
      dbpStoreIdToTrack,
      banners: [],
      currentType: impressionTypes.RECIPE_IMPRESSION,
      params,
    })

    if (params.action === 'trackOnUpdate' && !trackedBanners) {
      setTrackedBanners(true)
    }
  }

  const createObserver = ({ elementRef, bannerTrackObj, callAction }) => {
    if (
      callAction === 'onLoad' ||
      (callAction === 'afterUpdate' && trackedBanners)
    ) {
      createNewObserver({
        productRef: elementRef.current,
        trackType: trackingTypes.TRACK_ONCE,
        productTrackingData: bannerTrackObj,
        splitTrackingData: {
          ...bannerTrackObj,
          event_type: SPLIT_EVENT,
          [GEV.EVENT]: 'splitPromoImpression',
        },
        options: {
          root: null,
          rootMargin: '0px',
          threshold: 0.01,
        },
        impressionType: impressionTypes.RECIPE_IMPRESSION,
        splitImpressionType: impressionTypes.SPLIT_RECIPE_IMPRESSION,
      })

      if (trackedBanners) {
        setTrackedBanners(false)
      }
    }
  }

  useEffect(() => {
    triggerImpressionsTracking.subscribe(
      impressionTypes.RECIPE_IMPRESSION,
      data => {
        if (data === impressionTypes.RECIPE_IMPRESSION) {
          trackBannerImpressions()
        }
      }
    )
    return () => {
      triggerImpressionsTracking.unsubscribe(impressionTypes.RECIPE_IMPRESSION)
      trackBannerImpressions({ forceTrack: true, action: 'abandon' })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (!mounted.current) {
      mounted.current = true
    } else {
      trackBannerImpressions({
        action: 'trackOnUpdate',
        prevStoreId: getNCheck(checkoutAddress, 'clientId', '').toString(),
        prevDBPStoreId: getNCheck(checkoutAddress, 'storeId', '').toString(),
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checkoutAddress.storeId])

  return (
    <>
      <Header>
        <HeaderTitle>{headerTitle}</HeaderTitle>
        <ResultDescription>{headerDescription}</ResultDescription>
      </Header>
      <InfiniteScroll
        pageStart={0}
        loadMore={loadMore}
        hasMore={hasMore}
        loader={
          <LoaderContainer id="recipe-list-loader">
            <Loader />
          </LoaderContainer>
        }
      >
        <List>
          {items.map((item, index) => (
            <RecipeCard
              key={item.isin}
              item={item}
              pageType={pageType}
              bannerPosition={index + 1}
              trackBannerImpressions={trackBannerImpressions}
              label={label}
              sectionTitle={label}
              createObserverInfo={createObserver}
              ctaLocationMeta={ctaLocationMeta}
            />
          ))}
        </List>
      </InfiniteScroll>
    </>
  )
}

const RecipeListWrp = props => (
  <CheckoutAddressConsumer>
    {({ checkoutAddress }) => (
      <EventTrackingConsumer>
        {({ track, trackSplit }) => (
          <RecipeList
            {...props}
            track={track}
            trackSplit={trackSplit}
            checkoutAddress={checkoutAddress}
          />
        )}
      </EventTrackingConsumer>
    )}
  </CheckoutAddressConsumer>
)

export default RecipeListWrp
